import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";

import Table from "../../components/Table";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import SearchInput from "../../components/SearchInput";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import {
  textContainOnlySpaces,
  renderTextEllipsis,
  setPageMainTitle,
} from "../../utils/Helpers";
import { DeleteBgIcon, EditBgIcon, ViewBgIcon } from "../../utils/Icons";
import messages from "../../assets/locale/messages";
import {
  getSpecialtiesRequest,
  deleteSpecialtyRequest,
} from "../../store/Specialties/actions";
import "./Specialties.scss";

const Specialties = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.specialties.meta);
  const list = useSelector((state) => state.specialties.list);

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteSpecialtiesId, setdeleteSpecialtiesId] = useState(null);
  const { shared, specialties, pagesTitles } = messages[lang];

  useEffect(() => {
    setPageMainTitle("specialtiesManagement");
  }, []);

  const getSpecialties = (pageNo, search) => {
    dispatch(
      getSpecialtiesRequest({
        page: pageNo,
        items: 10,
        title: search,
        status: "accepted",
      })
    );
  };

  useEffect(() => {
    getSpecialties(page, searchValue);
  }, [page]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getSpecialties(1, searchValue);
    }
  }, [searchValue]);

  const handleEditSpecialties = (id) => {
    history.push(ROUTE_PATHS.editSpecialties.replace(":id", id));
  };

  const handleViewSpecialties = (id) => {
    history.push(ROUTE_PATHS.viewSpecialties.replace(":id", id));
  };

  const handleDeleteClick = (id) => {
    setdeleteSpecialtiesId(id);
    setOpenDeleteConfirm(true);
  };

  const handleDeleteSpecialties = () => {
    dispatch(
      deleteSpecialtyRequest({
        id: deleteSpecialtiesId,
        currentPage: meta.currentPage,
      })
    );
    setOpenDeleteConfirm(false);
  };

  const handleCloseDeleteConfirmModal = () => {
    setdeleteSpecialtiesId(null);
    setOpenDeleteConfirm(false);
  };

  const renderHeaderSection = (showActions) => (
    <div className="specialties-header d-flex justify-content-between mb-4">
      <div className="title-search d-flex flex-column flex-md-row">
        <Typography
          variant="h4"
          sx={{ flexGrow: 1 }}
          className={`page-title ${lang === "en" ? "me-2" : "ms-2"}`}
        >
          {pagesTitles.specialties}
        </Typography>
        {showActions && (
          <SearchInput
            name="search"
            value={searchValue}
            placeholder={specialties.list.search}
            onChange={(value) => {
              setSearchValue(value);
            }}
          />
        )}
      </div>
      {showActions && (
        <div className="add-btn-wrapper">
          <Button
            label={
              <span className="d-flex align-items-center">
                <Add fontSize="large" />
                <span className="ps-1 ">
                  {specialties.list.createSpecialtiesButton}
                </span>
              </span>
            }
            labelClass="fsize-16 text-white pe-2"
            onClick={() => {
              history.push(ROUTE_PATHS.addSpecialties);
            }}
            className="add-new-btn"
          />
        </div>
      )}
    </div>
  );

  const SpecialtiesList = () => {
    if (!isLoading && list?.length === 0 && !searchValue) {
      return (
        <>
          {renderHeaderSection(false)}
          <div className="bg-white rounded-4 specialties-page d-flex justify-content-center align-items-center flex-column">
            <EmptyState
              subTitle={shared.noData}
              description={specialties.list.noDataDescription}
              ButtonLabel={specialties.list.createSpecialtiesButton}
              handleBtnAction={() => {
                history.push(ROUTE_PATHS.addSpecialties);
              }}
            />
          </div>
        </>
      );
    } else if (!isLoading && searchValue && list?.length === 0) {
      return (
        <>
          {renderHeaderSection(true)}
          <div className="bg-white rounded-4 specialties-page d-flex justify-content-center align-items-center flex-column">
            <EmptyState subTitle={shared.noSearch} showActionButton={false} />
          </div>
        </>
      );
    } else {
      return (
        <>
          <div>
            {renderHeaderSection(true)}
            <div className="bg-white rounded-4 specialties-table-list">
              <Table
                className="rounded-4"
                headlines={[shared.lists.enTitle, shared.lists.arTitle]}
                hasActions={true}
                rows={list?.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="start font-regular text-dark-blue fsize-14 ">
                      {renderTextEllipsis(row.title_en || "-")}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14 ">
                      {renderTextEllipsis(row.title_ar || "-")}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14">
                      <div className="table-icons-container gap-4">
                        <EditBgIcon
                          className="pointer"
                          onClick={() => handleEditSpecialties(row.id)}
                        />
                        <ViewBgIcon
                          className="pointer"
                          onClick={() => {
                            handleViewSpecialties(row.id);
                          }}
                        />
                        <DeleteBgIcon
                          className="pointer"
                          onClick={() => {
                            handleDeleteClick(row.id);
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              />
              <div className="d-flex justify-content-center py-3">
                {meta?.pages > 1 && (
                  <Pagination
                    count={meta?.pages}
                    page={meta?.currentPage || page}
                    handleChange={(event, page) => {
                      setPage(page);
                    }}
                    defaultPage={1}
                  />
                )}
              </div>
            </div>
          </div>
          <Modal
            open={openDeleteConfirm}
            handleClose={handleCloseDeleteConfirmModal}
            title={specialties.deleteSpecialty.title}
            contentClass="mb-5 pb-2 text-primary"
            content={<span>{specialties.deleteSpecialty.deleteConfirmationMsg}</span>}
            confirmBtnTxt={shared.delete}
            maxWidth="xs"
            cancelBtnTxt={shared.cancel}
            isContentText={false}
            handleConfirm={handleDeleteSpecialties}
            handleCancel={handleCloseDeleteConfirmModal}
            modalClass="delete-specialties-modal"
            actionsClass="container px-4"
            confirmBtnClass="btn-secondary fsize-14 text-white py-2 "
            cancelBtnClass="fsize-14 py-2"
          />
        </>
      );
    }
  };
  return <div className="specialties-page">{SpecialtiesList()}</div>;
};

export default Specialties;
